import { State } from "@features/base/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  activateRequisite as activateRequisiteApi,
  ActivateRequisiteForm,
  ActivateRequisiteResponse,
  confirmOrderApi,
  createRequisite as createRequisiteApi,
  CreateRequisiteForm,
  DetailRequisite,
  DetailRequisiteResponse,
  enableRequisite as enableRequisiteApi,
  EnableRequisiteForm,
  getOrderRequisitesApi,
  getRequisite as getRequisiteApi,
  getRequisites as getRequisitesApi,
  OrderRequisite,
  removeRequisite as removeRequisiteApi,
  Requisite,
  RequisiteForm,
  RequisiteResponse,
  updateRequisite as updateRequisiteApi,
  UpdateRequisiteForm,
} from "./api";

type RequisitesState = State<{
  requisites: Requisite[] | null;
  page: number;
  totalCount: number;
  orderRequisites: OrderRequisite | null;
  orderConfirmStatus: string | null;
  isDeleting: boolean;
  isEnabling: boolean;
  requisite: DetailRequisite | null;
}>;

const initialState: RequisitesState = {
  requisites: null,
  loading: false,
  error: null,
  page: 1,
  totalCount: 0,
  orderRequisites: null,
  orderConfirmStatus: null,
  isDeleting: false,
  isEnabling: false,
  requisite: null,
};

export const getRequisites = createAsyncThunk<
  RequisiteResponse,
  { userId: string; data: RequisiteForm }
>("requisites/getRequisites", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await getRequisitesApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getOrderRequisites = createAsyncThunk<
  OrderRequisite,
  { orderId: string }
>("requisites/getOrderRequisites", async ({ orderId }, { rejectWithValue }) => {
  try {
    return await getOrderRequisitesApi(orderId);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const confirmOrder = createAsyncThunk<
  string,
  { orderId: string; userId: string }
>(
  "requisites/confirmOrder",
  async ({ orderId, userId }, { rejectWithValue }) => {
    try {
      return await confirmOrderApi(userId, orderId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createRequisite = createAsyncThunk<string, CreateRequisiteForm>(
  "requisites/createRequisite",
  async (data, { rejectWithValue }) => {
    try {
      return await createRequisiteApi(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removeRequisite = createAsyncThunk<string, string>(
  "requisites/removeRequisite",
  async (requisiteId, { rejectWithValue }) => {
    try {
      return await removeRequisiteApi(requisiteId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getRequisite = createAsyncThunk<DetailRequisiteResponse, string>(
  "requisites/getRequisite",
  async (requisiteId, { rejectWithValue }) => {
    try {
      return await getRequisiteApi(requisiteId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateRequisite = createAsyncThunk<string, UpdateRequisiteForm>(
  "requisites/updateRequisite",
  async (data, { rejectWithValue }) => {
    try {
      return await updateRequisiteApi(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const activateRequisite = createAsyncThunk<
  ActivateRequisiteResponse,
  { userId: string; data: ActivateRequisiteForm }
>(
  "requisites/activateRequisite",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await activateRequisiteApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const enableRequisite = createAsyncThunk<
  string,
  { id: string; data: EnableRequisiteForm }
>("requisites/enableRequisite", async ({ id, data }, { rejectWithValue }) => {
  try {
    return await enableRequisiteApi(id, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

const requisitesSlice = createSlice({
  name: "requisites",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRequisites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRequisites.fulfilled, (state, action) => {
        state.loading = false;
        state.requisites = action.payload.requisites;
        state.page = action.payload.page;
        state.totalCount = action.payload.count;
      })
      .addCase(getRequisites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getRequisite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRequisite.fulfilled, (state, action) => {
        state.loading = false;
        state.requisite = action.payload.data;
      })
      .addCase(getRequisite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getOrderRequisites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderRequisites.fulfilled, (state, action) => {
        state.loading = false;
        state.orderRequisites = action.payload;
      })
      .addCase(getOrderRequisites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(confirmOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderConfirmStatus = action.payload;
      })
      .addCase(confirmOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(removeRequisite.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(removeRequisite.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(removeRequisite.rejected, (state) => {
        state.isDeleting = false;
      })
      .addCase(enableRequisite.pending, (state) => {
        state.isEnabling = true;
      })
      .addCase(enableRequisite.fulfilled, (state, action) => {
        state.isEnabling = false;
        if (state.requisite) {
          state.requisite.at_work = action.meta.arg.data.at_work;
        }
      })
      .addCase(enableRequisite.rejected, (state, action) => {
        state.isEnabling = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.error = (action.payload as any).error as string;
      });
  },
});

export default requisitesSlice.reducer;
